import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { ViolationCard, MediaViewer } from '../components/ViolationCard';
import { StatusBadge, ViolationTypeBadge } from '../components/StatusBadge';
import { LocationDisplay } from '../components/LocationPicker';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { violationService, mediaUrl } from '../services/api';
import { 
    Car, 
    MapPin, 
    FileText, 
    Clock, 
    CheckCircle2, 
    XCircle,
    Loader2,
    ScanLine,
    Calendar,
    Plus
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

export default function MyReports() {
    const { user, token } = useAuth();
    const navigate = useNavigate();
    const [violations, setViolations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedViolation, setSelectedViolation] = useState(null);
    const [showMediaViewer, setShowMediaViewer] = useState(false);
    const [mediaIndex, setMediaIndex] = useState(0);
    const [appealReason, setAppealReason] = useState('');
    const [appealing, setAppealing] = useState(false);

    const handleAppeal = async () => {
        if (!selectedViolation) return;
        if (!appealReason.trim()) {
            toast.error('Please explain why you are appealing');
            return;
        }
        setAppealing(true);
        try {
            await violationService.appeal(selectedViolation.id, appealReason.trim());
            toast.success('Appeal submitted — an officer will re-review your report.');
            setAppealReason('');
            setSelectedViolation(null);
            fetchViolations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit appeal');
        } finally {
            setAppealing(false);
        }
    };

    const fetchViolations = useCallback(async () => {
        try {
            const response = await violationService.list();
            setViolations(response.data);
        } catch (error) {
            console.error('Failed to fetch violations');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchViolations();
    }, [fetchViolations]);

    const stats = {
        total: violations.length,
        pending: violations.filter(v => v.status === 'pending').length,
        approved: violations.filter(v => v.status === 'approved').length,
        rejected: violations.filter(v => v.status === 'rejected').length
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">My Reports</h1>
                        <p className="text-muted-foreground mt-1">
                            View and track all your submitted violation reports
                        </p>
                    </div>
                    <Button 
                        onClick={() => navigate('/dashboard')}
                        data-testid="new-report-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Report
                    </Button>
                </div>

                {/* Stats Summary Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.total}</p>
                                    <p className="text-xs text-muted-foreground">Total Reports</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-warning">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-warning" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                    <p className="text-xs text-muted-foreground">Pending</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.approved}</p>
                                    <p className="text-xs text-muted-foreground">Approved</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-red-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                                    <XCircle className="w-5 h-5 text-red-500" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{stats.rejected}</p>
                                    <p className="text-xs text-muted-foreground">Rejected</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Violations List */}
                <Card>
                    <CardHeader>
                        <CardTitle>Violation Reports</CardTitle>
                        <CardDescription>
                            Filter and view your submitted reports by status
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs defaultValue="all">
                            <TabsList className="mb-4">
                                <TabsTrigger value="all" data-testid="tab-all">
                                    All ({stats.total})
                                </TabsTrigger>
                                <TabsTrigger value="pending" data-testid="tab-pending">
                                    Pending ({stats.pending})
                                </TabsTrigger>
                                <TabsTrigger value="approved" data-testid="tab-approved">
                                    Approved ({stats.approved})
                                </TabsTrigger>
                                <TabsTrigger value="rejected" data-testid="tab-rejected">
                                    Rejected ({stats.rejected})
                                </TabsTrigger>
                            </TabsList>

                            {['all', 'pending', 'approved', 'rejected'].map(tab => (
                                <TabsContent key={tab} value={tab}>
                                    {loading ? (
                                        <div className="flex items-center justify-center py-12">
                                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                        </div>
                                    ) : (
                                        <div className="space-y-4 stagger-children">
                                            {violations
                                                .filter(v => tab === 'all' || v.status === tab)
                                                .map(violation => (
                                                    <ViolationCard
                                                        key={violation.id}
                                                        violation={violation}
                                                        token={token}
                                                        onClick={() => setSelectedViolation(violation)}
                                                    />
                                                ))
                                            }
                                            {violations.filter(v => tab === 'all' || v.status === tab).length === 0 && (
                                                <div className="text-center py-12">
                                                    <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                                                    <p className="text-muted-foreground">
                                                        No {tab === 'all' ? '' : tab} reports found
                                                    </p>
                                                    {tab === 'all' && (
                                                        <Button 
                                                            variant="outline" 
                                                            className="mt-4"
                                                            onClick={() => navigate('/dashboard')}
                                                        >
                                                            <Plus className="w-4 h-4 mr-2" />
                                                            Submit your first report
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </TabsContent>
                            ))}
                        </Tabs>
                    </CardContent>
                </Card>
            </main>

            {/* Violation Detail Dialog */}
            <Dialog open={!!selectedViolation} onOpenChange={() => setSelectedViolation(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selectedViolation && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Car className="w-5 h-5" />
                                    {selectedViolation.vehicle_number}
                                </DialogTitle>
                                <DialogDescription>
                                    Violation Report Details
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-6">
                                <div className="flex flex-wrap gap-2">
                                    <ViolationTypeBadge type={selectedViolation.violation_type} />
                                    <StatusBadge status={selectedViolation.status} />
                                    {selectedViolation.ocr_detected_plate && (
                                        <span className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                                            <ScanLine className="w-3 h-3" />
                                            OCR: {selectedViolation.ocr_detected_plate}
                                        </span>
                                    )}
                                </div>

                                {selectedViolation.description && (
                                    <div>
                                        <p className="text-sm font-medium mb-1">Description</p>
                                        <p className="text-sm text-muted-foreground">
                                            {selectedViolation.description}
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    {selectedViolation.location && (
                                        <div>
                                            <p className="font-medium flex items-center gap-1 mb-1">
                                                <MapPin className="w-4 h-4" /> Location
                                            </p>
                                            <p className="text-muted-foreground">{selectedViolation.location}</p>
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-medium flex items-center gap-1 mb-1">
                                            <Calendar className="w-4 h-4" /> Reported
                                        </p>
                                        <p className="text-muted-foreground">
                                            {format(new Date(selectedViolation.created_at), 'PPp')}
                                        </p>
                                    </div>
                                </div>

                                {/* Map Display */}
                                {selectedViolation.latitude && selectedViolation.longitude && (
                                    <LocationDisplay
                                        latitude={selectedViolation.latitude}
                                        longitude={selectedViolation.longitude}
                                        locationName={selectedViolation.location}
                                    />
                                )}

                                {/* Media */}
                                {selectedViolation.media_files?.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium mb-2">Evidence</p>
                                        <div className="grid grid-cols-3 gap-2">
                                            {selectedViolation.media_files.map((media, idx) => {
                                                const url = mediaUrl(media.path, token);
                                                return (
                                                    <button
                                                        key={media.id}
                                                        className="relative aspect-square rounded-lg overflow-hidden bg-muted group"
                                                        onClick={() => {
                                                            setMediaIndex(idx);
                                                            setShowMediaViewer(true);
                                                        }}
                                                        data-testid={`detail-media-${idx}`}
                                                    >
                                                        {media.type === 'video' ? (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <FileText className="w-8 h-8 text-muted-foreground" />
                                                            </div>
                                                        ) : (
                                                            <img
                                                                src={url}
                                                                alt={media.original_name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Review Info */}
                                {selectedViolation.status !== 'pending' && (
                                    <div className="p-4 rounded-lg bg-muted/50 border">
                                        <p className="text-sm font-medium mb-2">Review Details</p>
                                        <div className="space-y-2 text-sm">
                                            <p>
                                                <span className="text-muted-foreground">Reviewed by:</span>{' '}
                                                {selectedViolation.reviewer_name || 'Officer'}
                                            </p>
                                            {selectedViolation.reviewed_at && (
                                                <p>
                                                    <span className="text-muted-foreground">Reviewed on:</span>{' '}
                                                    {format(new Date(selectedViolation.reviewed_at), 'PPp')}
                                                </p>
                                            )}
                                            {selectedViolation.review_remarks && (
                                                <p>
                                                    <span className="text-muted-foreground">Remarks:</span>{' '}
                                                    {selectedViolation.review_remarks}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {selectedViolation.status === 'approved' && selectedViolation.api_response && (
                                    <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                                        <p className="text-sm font-medium text-green-600 mb-1 flex items-center gap-1">
                                            <CheckCircle2 className="w-4 h-4" />
                                            Sent to Government System
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            Challan reference: {selectedViolation.api_response.reference_id}
                                        </p>
                                    </div>
                                )}

                                {/* Appeal a rejected decision */}
                                {selectedViolation.status === 'rejected' && (
                                    <div className="p-4 rounded-lg border space-y-3">
                                        <p className="text-sm font-medium">Disagree with this decision?</p>
                                        <Textarea
                                            placeholder="Explain why this violation should be reconsidered…"
                                            value={appealReason}
                                            onChange={(e) => setAppealReason(e.target.value)}
                                            rows={3}
                                            data-testid="appeal-reason-input"
                                        />
                                        <Button
                                            onClick={handleAppeal}
                                            disabled={appealing || !appealReason.trim()}
                                            data-testid="submit-appeal-btn"
                                        >
                                            {appealing ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                                            ) : (
                                                'Submit Appeal'
                                            )}
                                        </Button>
                                    </div>
                                )}

                                {selectedViolation.status === 'appealed' && (
                                    <div className="p-4 rounded-lg bg-info/10 border border-info/30">
                                        <p className="text-sm font-medium text-info">Appeal under review</p>
                                        {selectedViolation.appeal?.reason && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                "{selectedViolation.appeal.reason}"
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Media Viewer */}
            {showMediaViewer && selectedViolation && (
                <MediaViewer
                    mediaFiles={selectedViolation.media_files}
                    token={token}
                    onClose={() => setShowMediaViewer(false)}
                />
            )}
        </div>
    );
}
